import React, { useEffect, useMemo, useState } from "react";
import {
  CAlert,
  CBadge,
  CButton,
  CCol,
  CFormLabel,
  CFormSelect,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CRow,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { apiClient } from "services/Api";

const SECTION_LABELS = {
  project: "Dados da candidatura",
  activities: "Atividades",
  technicians: "Pessoal técnico",
  team: "Afetação (equipa)",
};

const ACTION_BADGES = {
  create: { color: "success", label: "Criar" },
  update: { color: "warning", label: "Atualizar" },
  replace: { color: "info", label: "Substituir" },
  delete: { color: "danger", label: "Apagar" },
  keep: { color: "secondary", label: "Manter" },
};
const ACTIONS = Object.keys(ACTION_BADGES);

const MODE_LABELS = { real: "real", planned: "planeado" };

// Nomes das colunas tal como aparecem nas grelhas da candidatura.
// Os anos (ETI da equipa) são mostrados tal como estão ("2025", ...).
const FIELD_LABELS = {
  projectStartDate: "Data de início",
  projectEndDate: "Data de fim",
  designation: "Designação",
  startDate: "Data Início",
  endDate: "Data Fim",
  nature: "Grau de Maturidade",
  maturityDegree: "Grau de Maturidade",
  activityDegree: "Grau de Maturidade",
  component: "Componente",
  typology: "Componente",
  activityType: "Inovação",
  inovationType: "Inovação",
  name: "Nome/Perfil",
  nif: "NIF",
  location: "Estab.",
};

const errorMessage = (error) =>
  error?.response?.data?.error || "Não foi possível obter os dados do ROBERTO.";

const projectLabel = (project) =>
  `${project.client_name} › ${project.name}${project.code ? ` · ${project.code}` : ""}`;

// Junta itens consecutivos com o mesmo ``group`` (ex.: a pessoa na afetação).
// Itens sem ``group`` ficam num único bloco sem cabeçalho.
const groupItems = (items) =>
  items.reduce((groups, item) => {
    const group = item.group ?? null;
    const last = groups[groups.length - 1];
    if (last && last.group === group) last.items.push(item);
    else groups.push({ group, items: [item] });
    return groups;
  }, []);

const formatValue = (value) =>
  value === null || value === undefined || value === "" ? "-" : String(value);

/**
 * Modal "Carregar Dados › ROBERTO": importa datas, atividades, pessoal técnico e afetação a partir do projeto
 * ROBERTO de um beneficiário. Mostra uma pré-visualização antes de gravar; a
 * gravação é feita (atomicamente) no backend e a página é recarregada.
 */
const RobertoImportModal = ({
  id,
  typology = "idt",
  visible,
  onClose,
  hasUnsavedChanges = false,
  onImported,
  setToast,
}) => {
  const baseUrl = `/application/${typology}/${id}/import-roberto/`;
  const [options, setOptions] = useState(null);
  const [beneficiary, setBeneficiary] = useState("");
  const [project, setProject] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});

  const selectedBeneficiary = useMemo(
    () =>
      options?.beneficiaries?.find((b) => String(b.id) === String(beneficiary)),
    [options, beneficiary],
  );
  // Sugestões (cliente com nome próximo do beneficiário) primeiro; os restantes
  // projetos geridos ficam disponíveis para escolha manual.
  const suggestedProjects = useMemo(
    () => selectedBeneficiary?.projects ?? [],
    [selectedBeneficiary],
  );
  const otherProjects = useMemo(() => {
    const suggestedIds = new Set(suggestedProjects.map((p) => String(p.id)));
    return (options?.projects ?? []).filter(
      (p) => !suggestedIds.has(String(p.id)),
    );
  }, [options, suggestedProjects]);

  useEffect(() => {
    if (!visible) return;
    setPreview(null);
    setError(null);
    setLoading(true);
    apiClient
      .get(baseUrl)
      .then(({ data }) => setOptions(data))
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  }, [visible, baseUrl]);

  const close = () => {
    if (saving) return;
    onClose?.();
  };

  useEffect(() => {
    setPreview(null);
    setError(null);
    if (!selectedBeneficiary) {
      setProject("");
      return;
    }
    const list = selectedBeneficiary.projects ?? [];
    const last = selectedBeneficiary.last_import?.project_id;
    const lastProject = (options?.projects ?? []).find(
      (p) => String(p.id) === String(last),
    );
    const preferred = lastProject
      ? lastProject.id
      : list.length === 1
        ? list[0].id
        : null;
    setProject(preferred != null ? String(preferred) : "");
  }, [selectedBeneficiary, options]);

  useEffect(() => {
    setPreview(null);
    setError(null);
  }, [project]);

  const loadPreview = () => {
    setLoading(true);
    setError(null);
    apiClient
      .get(baseUrl, { params: { beneficiary, project } })
      .then(({ data }) => {
        setPreview(data);
        setExpanded({});
      })
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  };

  const confirm = () => {
    setSaving(true);
    setError(null);
    apiClient
      .post(baseUrl, { beneficiary, project, plan_token: preview?.plan_token })
      .then(({ data }) => {
        onClose?.();
        onImported?.(data);
      })
      .catch((err) => {
        // 409: o plano mudou desde a pré-visualização; é preciso revê-lo.
        if (err?.response?.status === 409) setPreview(null);
        setError(errorMessage(err));
        setToast?.({
          title: "Importar candidatura",
          message: errorMessage(err),
          type: "error",
        });
      })
      .finally(() => setSaving(false));
  };

  const renderSection = (key) => {
    const items = preview.sections?.[key] ?? [];
    const counts = preview.summary?.[key] ?? {};
    const isOpen = !!expanded[key];
    return (
      <CTableRow key={key}>
        <CTableDataCell>
          <div
            role="button"
            className="fw-semibold"
            onClick={() => setExpanded((prev) => ({ ...prev, [key]: !isOpen }))}
          >
            {isOpen ? "▾" : "▸"} {SECTION_LABELS[key]}
          </div>
          {isOpen &&
            groupItems(items).map(({ group, items: groupItemsList }, index) => (
              <div key={index} className="small mt-2">
                {group && <div className="fw-semibold">{group}</div>}
                <ul className="mb-0">
                  {groupItemsList.map((item, itemIndex) => (
                    <li key={itemIndex}>
                      <CBadge
                        color={ACTION_BADGES[item.action]?.color}
                        className="me-2"
                      >
                        {ACTION_BADGES[item.action]?.label}
                      </CBadge>
                      {item.label}
                      {item.changes?.length > 0 && (
                        <ul className="text-muted ps-4 mb-1">
                          {item.changes.map((c) => (
                            <li key={c.field}>
                              {FIELD_LABELS[c.field] ?? c.field}:{" "}
                              {formatValue(c.before)} → {formatValue(c.after)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </CTableDataCell>
        {ACTIONS.map((action) => (
          <CTableDataCell key={action} className="text-center">
            {counts[action] ?? 0}
          </CTableDataCell>
        ))}
      </CTableRow>
    );
  };

  return (
    <CModal size="xl" visible={visible} onClose={close} scrollable>
      <CModalHeader>
        <CModalTitle>Importar candidatura do ROBERTO</CModalTitle>
      </CModalHeader>
      <CModalBody>
        {hasUnsavedChanges && (
          <CAlert color="warning">
            Tem alterações por guardar. A importação grava os dados no servidor
            e recarrega a página: as alterações não guardadas serão perdidas.
          </CAlert>
        )}

        <CRow className="g-3 mb-3">
          <CCol md={6}>
            <CFormLabel>Beneficiário</CFormLabel>
            <CFormSelect
              value={beneficiary}
              disabled={!options || saving}
              onChange={(e) => setBeneficiary(e.target.value)}
            >
              <option value="">Selecione o beneficiário</option>
              {(options?.beneficiaries ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                  {b.nif ? ` (${b.nif})` : ""}
                </option>
              ))}
            </CFormSelect>
          </CCol>
          <CCol md={6}>
            <CFormLabel>Projeto ROBERTO</CFormLabel>
            <CFormSelect
              value={project}
              disabled={!selectedBeneficiary || saving}
              onChange={(e) => setProject(e.target.value)}
            >
              <option value="">Selecione o projeto</option>
              {suggestedProjects.length > 0 && (
                <optgroup label="Sugeridos para este beneficiário">
                  {suggestedProjects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {projectLabel(p)}
                    </option>
                  ))}
                </optgroup>
              )}
              {otherProjects.length > 0 && (
                <optgroup label="Outros projetos">
                  {otherProjects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {projectLabel(p)}
                    </option>
                  ))}
                </optgroup>
              )}
            </CFormSelect>
          </CCol>
        </CRow>

        {error && <CAlert color="danger">{error}</CAlert>}

        {loading && (
          <div className="text-center py-3">
            <CSpinner color="primary" />
          </div>
        )}

        {preview && !loading && (
          <>
            <p className="text-muted small">
              Projeto <strong>{preview.project?.name}</strong> (
              {preview.project?.date_from} a {preview.project?.date_to}),
              afetação calculada com base no{" "}
              {MODE_LABELS[preview.project?.mode] ?? preview.project?.mode} como
              na tabela de resumo do ROBERTO (por atividade).
            </p>
            {/* Mesmo aspeto das DynamicTable da candidatura. */}
            <CTable striped bordered hover responsive className="align-middle">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell className="text-center align-middle">
                    Secção
                  </CTableHeaderCell>
                  {ACTIONS.map((action) => (
                    <CTableHeaderCell
                      key={action}
                      className="text-center align-middle"
                      style={{ width: "10%" }}
                    >
                      {ACTION_BADGES[action].label}
                    </CTableHeaderCell>
                  ))}
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {Object.keys(SECTION_LABELS).map(renderSection)}
              </CTableBody>
            </CTable>

            {preview.infos?.length > 0 && (
              <CAlert color="info" className="small">
                <ul className="mb-0 ps-3">
                  {preview.infos.map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </ul>
              </CAlert>
            )}
            {preview.warnings?.length > 0 && (
              <CAlert color="warning" className="small">
                <div className="fw-semibold mb-1">Avisos</div>
                <ul className="mb-0 ps-3">
                  {preview.warnings.map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </ul>
              </CAlert>
            )}
          </>
        )}
      </CModalBody>
      <CModalFooter>
        <CButton
          color="secondary"
          variant="outline"
          onClick={close}
          disabled={saving}
        >
          Cancelar
        </CButton>
        {!preview ? (
          <CButton
            color="primary"
            disabled={!beneficiary || !project || loading}
            onClick={loadPreview}
          >
            Pré-visualizar
          </CButton>
        ) : (
          <CButton color="primary" disabled={saving} onClick={confirm}>
            {saving && <CSpinner size="sm" className="me-2" />}
            Confirmar importação
          </CButton>
        )}
      </CModalFooter>
    </CModal>
  );
};

export default RobertoImportModal;
